/**
 * Render-time hardening for arbitrary user text. JSX auto-escapes plain
 * characters, but does not protect against:
 *   - Unicode RTL spoofing (e.g. `‮` reverses display)
 *   - Zero-width characters (`​`, `‌`, `‍`)
 *   - C0/C1 control chars that some terminals/loggers interpret
 *   - Mixed-script lookalike attacks (NFKC normalization helps)
 *
 * Use anywhere user-provided text crosses a trust boundary.
 *
 *   <SafeText value={userInput} />
 *   safeText(userInput)
 */

// Bidi controls + invisible characters that have no legitimate use in mesh-* apps.
// Range derived from Unicode Bidirectional Algorithm + Default Ignorable Code Points.
// eslint-disable-next-line no-misleading-character-class
const STRIP_RE =
  /[ --​-‏‪-‮⁠-⁯﻿]/g;

export type SafeTextOptions = {
  /** Trim leading/trailing whitespace. Default: true. */
  trim?: boolean;
  /** Collapse runs of whitespace into one space. Default: false. */
  collapseWhitespace?: boolean;
  /** Apply NFKC normalization to neutralize lookalike characters. Default: true. */
  normalize?: boolean;
  /** Maximum output length (truncates with `…`). Default: no limit. */
  maxLength?: number;
};

/**
 * Sanitize a user-provided string for display. Idempotent — calling twice
 * returns the same result. Never throws.
 */
export function safeText(input: unknown, opts: SafeTextOptions = {}): string {
  if (input == null) return "";
  let s = String(input);
  if (opts.normalize !== false && typeof s.normalize === "function") {
    try {
      s = s.normalize("NFKC");
    } catch {
      /* ignore */
    }
  }
  s = s.replace(STRIP_RE, "");
  if (opts.collapseWhitespace) s = s.replace(/\s+/g, " ");
  if (opts.trim !== false) s = s.trim();
  if (opts.maxLength != null && s.length > opts.maxLength) {
    s = s.slice(0, Math.max(0, opts.maxLength - 1)) + "…";
  }
  return s;
}

type SafeTextProps = {
  value: unknown;
  className?: string;
  /** Render as `<span>` by default, or any inline element. */
  as?: "span" | "p" | "strong" | "em" | "code";
} & SafeTextOptions;

export function SafeText({
  value,
  className,
  as = "span",
  ...rest
}: SafeTextProps) {
  const text = safeText(value, rest);
  const Tag = as;
  return <Tag className={className}>{text}</Tag>;
}
